import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import {
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
} from "@mui/material";
import React, { ReactElement, useEffect } from "react";
import { useNavigate } from "react-router-dom";

import { useActiveClient } from "../../context/active-client-context";
import { User } from "../../generated/gql/graphql";
import { useLocalStorage } from "../../hooks/use-local-storage";
import { LOCAL_STORAGE_ACTIVE_CLIENT_ID } from "../../providers/active-client-provider";

export type ActiveClientSelectorUser = Pick<
  User,
  "activeClientId" | "clientIds" | "clients" | "isAdmin"
>;

type Props = {
  user: ActiveClientSelectorUser;
};

export default function ActiveClientSelector(props: Props): ReactElement {
  const { user } = props;
  const { activeClientId, setActiveClientId } = useActiveClient();
  const navigate = useNavigate();
  const { getItem, setItem, removeItem } = useLocalStorage(
    LOCAL_STORAGE_ACTIVE_CLIENT_ID
  );

  const sortedClients =
    user.clients?.slice().sort((a, b) => a.name.localeCompare(b.name)) ?? [];

  useEffect(() => {
    const storedValue = getItem();

    if (storedValue) {
      if (user.clients?.some((client) => client.id === storedValue)) {
        setActiveClientId(storedValue);
        return;
      }
      // Stored client no longer belongs to the user
      removeItem();
    }

    if (user.activeClientId) {
      setActiveClientId(user.activeClientId);
      return;
    }

    if (user.clients?.length === 1) {
      setActiveClientId(user.clients[0].id);
    }
  }, [user.activeClientId, user.clients]);

  if (!user.clientIds || user.clientIds.length <= 1) {
    return <></>;
  }

  const handleActiveClientChange = (event: SelectChangeEvent<string>): void => {
    const { value } = event.target;

    setActiveClientId(value);
    setItem(value);

    if (!user.isAdmin) {
      navigate("/");
    }
  };

  return (
    <FormControl sx={{ m: 1, minWidth: 120 }} size="small">
      <InputLabel>Workspace</InputLabel>
      <Select
        value={activeClientId ?? ""}
        label="Workspace"
        onChange={handleActiveClientChange}
        IconComponent={ExpandMoreIcon}
      >
        {sortedClients.map((client) => (
          <MenuItem key={client.id} value={client.id}>
            {client.name}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
}
